<template>
  <!-- Modal Overlay -->
  <Teleport to="body">
    <Transition name="modal" appear>
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click="handleOverlayClick"
      >
        <!-- Backdrop/Overlay -->
        <div
          class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
          aria-hidden="true"
        />
        
        <!-- Modal Container -->
        <div
          ref="modalRef"
          v-bind="$attrs"
          :class="[
            'relative bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden',
            'transform transition-all duration-300 ease-out',
            'w-full max-w-md mx-auto',
            sizeClass,
            'animate-modal-enter'
          ]"
          role="dialog"
          :aria-labelledby="titleId"
          :aria-describedby="contentId"
          aria-modal="true"
          @click.stop
        >
          <!-- Modal Header -->
          <div
            v-if="$slots.header || title || closable"
            class="flex items-center justify-between p-6 pb-4 border-b border-gray-100"
          >
            <div class="flex-1">
              <slot name="header">
                <h2
                  v-if="title"
                  :id="titleId"
                  class="text-xl font-heading font-semibold text-anthracite-dark"
                >
                  {{ title }}
                </h2>
              </slot>
            </div>
            
            <!-- Close Button -->
            <button
              v-if="closable"
              type="button"
              class="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
              @click="handleClose"
              :aria-label="closeLabel"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Modal Content -->
          <div
            :id="contentId"
            class="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]"
            :class="{ 'pt-6': !($slots.header || title) }"
          >
            <slot />
          </div>
          
          <!-- Modal Footer -->
          <div
            v-if="$slots.footer"
            class="flex items-center justify-end gap-3 p-6 pt-4 border-t border-gray-100 bg-gray-50/50"
          >
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { nextTick } from 'vue';

export default {
  name: 'BaseModal',
  inheritAttrs: false,
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['xs', 'sm', 'md', 'lg', 'xl', '2xl', 'full', 'wide', '75vw', '80vw'].includes(v)
    },
    closable: {
      type: Boolean,
      default: true
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    },
    closeLabel: {
      type: String,
      default: 'Cerrar modal'
    },
    persistent: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'close', 'open'],
  data() {
    return {
      titleId: `modal-title-${Math.random().toString(36).substr(2, 9)}`,
      contentId: `modal-content-${Math.random().toString(36).substr(2, 9)}`,
      previousActiveElement: null
    };
  },
  computed: {
    sizeClass() {
      const sizes = {
        xs: 'max-w-xs',
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-4xl',
        '2xl': 'max-w-2xl',
        full: 'max-w-[95vw] max-h-[95vh]',
        wide: 'w-full max-w-full sm:max-w-[95vw] md:max-w-[95vw] lg:max-w-[80vw]',
        '75vw': 'w-full max-w-[90vw] sm:max-w-[85vw] md:max-w-[80vw] lg:max-w-[75vw]',
        '80vw': 'w-full max-w-[85vw] sm:max-w-[80vw] md:max-w-[80vw] lg:max-w-[80vw]'
      };
      return sizes[this.size] || sizes.md;
    }
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (newVal) {
          this.onOpen();
        } else {
          this.onClose();
        }
      },
      immediate: true
    }
  },
  mounted() {
    if (this.closeOnEscape) {
      document.addEventListener('keydown', this.handleEscapeKey);
    }
  },
  beforeUnmount() {
    if (this.closeOnEscape) {
      document.removeEventListener('keydown', this.handleEscapeKey);
    }
    this.restoreFocus();
    this.enableBodyScroll();
  },
  methods: {
    handleClose() {
      if (this.persistent) return;
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    handleOverlayClick() {
      if (this.closeOnOverlay && !this.persistent) {
        this.handleClose();
      }
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.modelValue && !this.persistent) {
        this.handleClose();
      }
    },
    async onOpen() {
      this.$emit('open');
      this.saveFocus();
      this.disableBodyScroll();
      
      await nextTick();
      this.focusModal();
    },
    onClose() {
      this.restoreFocus();
      this.enableBodyScroll();
    },
    saveFocus() {
      this.previousActiveElement = document.activeElement;
    },
    restoreFocus() {
      if (this.previousActiveElement && this.previousActiveElement.focus) {
        this.previousActiveElement.focus();
      }
    },
    focusModal() {
      if (this.$refs.modalRef) {
        // Focus the modal container or the first focusable element
        const focusableElements = this.$refs.modalRef.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else {
          this.$refs.modalRef.focus();
        }
      }
    },
    disableBodyScroll() {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
    },
    enableBodyScroll() {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    },
    getScrollbarWidth() {
      // Create temporary elements to measure scrollbar width
      const outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll';
      outer.style.msOverflowStyle = 'scrollbar';
      document.body.appendChild(outer);
      
      const inner = document.createElement('div');
      outer.appendChild(inner);
      
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      outer.parentNode.removeChild(outer);
      
      return scrollbarWidth;
    }
  }
};
</script>

<style scoped>
/* Modal transition animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Modal content animations */
.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  opacity: 0;
  transform: scale(0.9) translateY(-10px);
}

/* Custom animation for smooth entrance */
@keyframes modal-enter {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-modal-enter {
  animation: modal-enter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

/* Focus styles for accessibility */
.relative:focus {
  outline: 2px solid theme('colors.primary.DEFAULT');
  outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .relative {
    margin: 1rem;
    max-height: calc(100vh - 2rem);
  }
  
  /* Wide modal takes full width on mobile */
  .w-full.max-w-full.sm\:max-w-\[95vw\].md\:max-w-\[95vw\].lg\:max-w-\[80vw\] {
    max-width: calc(100vw - 2rem) !important;
    margin: 1rem;
  }
}

@media (min-width: 641px) and (max-width: 1023px) {
  /* Tablet styles for wide modal */
  .w-full.max-w-full.sm\:max-w-\[95vw\].md\:max-w-\[95vw\].lg\:max-w-\[80vw\] {
    max-width: 95vw !important;
  }
}

@media (min-width: 1024px) {
  /* Desktop styles for wide modal */
  .w-full.max-w-full.sm\:max-w-\[95vw\].md\:max-w-\[95vw\].lg\:max-w-\[80vw\] {
    max-width: 80vw !important;
  }
  
  /* Desktop styles for 75vw modal */
  .w-full.max-w-\[90vw\].sm\:max-w-\[85vw\].md\:max-w-\[80vw\].lg\:max-w-\[75vw\] {
    max-width: 75vw !important;
  }
}

/* Smooth scrolling for modal content */
.overflow-y-auto {
  scroll-behavior: smooth;
}

/* Custom scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
